import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { format } from "date-fns";
import {
  Mail, Upload, Globe, Send, Calendar as CalendarIcon, BarChart3,
  LogOut, Plus, Eye, Trash2, RefreshCw, Users, MousePointerClick,
  MailOpen, Clock, CheckCircle2, AlertCircle, Loader2, FileSpreadsheet,
  ChevronRight, ChevronLeft, Sparkles, LayoutDashboard, History, Settings, Menu, X,
  Minus, PlusCircle, EyeOff, ChevronDown, ChevronUp, Image as ImageIcon, Palette, Share2, Copy, Download, Facebook, Instagram,
  HelpCircle, Maximize2, Minimize2, AlertTriangle, MessageSquare
} from "lucide-react";
import { ImagePicker } from "@/components/ImagePicker";
import { SectionImageManager } from "@/components/SectionImageManager";
import { GettingStartedPage, GettingStartedChecklist, TroubleshootingHelp } from "@/components/GettingStartedGuide";
import { useAuth, API } from "@/contexts/AuthContext";
import { Layout } from "@/components/layout/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OnboardingWizard = () => {
  const { authAxios, user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [brand, setBrand] = useState({
    business_name: user?.company_name || "",
    slug: "",
    reply_to_email: user?.email || "",
    brand_color: "#0d9488",
    shared_sender_domain: "swiftlett.com",
  });
  const [slugStatus, setSlugStatus] = useState({ checking: false, available: null, message: "" });
  const slugTimer = React.useRef(null);

  // Load the auto-created brand on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await authAxios.get("/brand");
        setBrand((prev) => ({
          ...prev,
          ...res.data,
          business_name: res.data.business_name || prev.business_name,
        }));
      } catch {/* ignore */}
    })();
    // eslint-disable-next-line
  }, []);

  const checkSlug = (value) => {
    if (slugTimer.current) clearTimeout(slugTimer.current);
    setSlugStatus({ checking: true, available: null, message: "" });
    slugTimer.current = setTimeout(async () => {
      try {
        const res = await authAxios.get(`/brand/check-slug?slug=${encodeURIComponent(value)}`);
        setSlugStatus({
          checking: false,
          available: res.data.available,
          message: res.data.available ? "Looks great!" : (res.data.reason || "Taken — pick another"),
        });
      } catch (err) {
        // Slug check failed (network blip or 4xx) — fall back to a neutral state
        // so the user can still proceed; log for diagnostics but don't toast.
        console.debug("Slug availability check failed:", err?.message);
        setSlugStatus({ checking: false, available: null, message: "" });
      }
    }, 350);
  };

  const handleSlugChange = (raw) => {
    const cleaned = (raw || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    setBrand({ ...brand, slug: cleaned });
    if (cleaned.length >= 2) checkSlug(cleaned);
    else setSlugStatus({ checking: false, available: null, message: "" });
  };

  const saveBrand = async () => {
    setSaving(true);
    try {
      const res = await authAxios.put("/brand", {
        business_name: brand.business_name,
        slug: brand.slug || null,
        reply_to_email: brand.reply_to_email || null,
        brand_color: brand.brand_color,
      });
      setBrand((prev) => ({ ...prev, ...res.data }));
      return res.data;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const goNext = async () => {
    if (step === 1) {
      if (!brand.business_name?.trim()) { toast.error("Business name is required"); return; }
      setStep(2);
    } else if (step === 2) {
      try {
        await saveBrand();
        setStep(3);
      } catch {/* already toasted */}
    }
  };

  const finishOnboarding = async () => {
    setSaving(true);
    try {
      await authAxios.post("/auth/complete-onboarding");
      await refreshUser();
      toast.success("You're all set!");
      navigate("/campaign/new");
    } catch {
      toast.error("Could not complete onboarding");
    } finally {
      setSaving(false);
    }
  };

  const skipForNow = async () => {
    try {
      await authAxios.post("/auth/complete-onboarding");
      await refreshUser();
      navigate("/dashboard");
    } catch {
      toast.error("Couldn't skip right now — please try again");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#003858] via-[#006080] to-[#119AB3] flex items-center justify-center p-6 relative overflow-hidden" data-testid="onboarding-wizard">
      {/* Decorative */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#B7EB1B]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#119AB3]/30 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 w-full max-w-2xl">
        {/* Brand header */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <Mail className="w-6 h-6 text-afa-lime" />
          </div>
          <div className="flex flex-col">
            <span className="text-xl font-bold text-white leading-tight" style={{fontFamily: 'Manrope'}}>Simply Smarter</span>
            <span className="text-sm font-medium text-afa-lime leading-tight">Email</span>
          </div>
        </div>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {[1, 2, 3].map((n) => (
            <div
              key={n}
              data-testid={`onboarding-dot-${n}`}
              className={`h-2 rounded-full transition-all ${step === n ? "w-8 bg-afa-lime" : step > n ? "w-2 bg-afa-lime/60" : "w-2 bg-white/20"}`}
            />
          ))}
        </div>

        <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 shadow-2xl">
          {step === 1 && (
            <div data-testid="onboarding-step-1">
              <h2 className="text-2xl font-bold text-white mb-2" style={{fontFamily: 'Manrope'}}>Tell us about your business</h2>
              <p className="text-white/70 mb-6">This is what your recipients will see in their inbox.</p>

              <Label className="text-sm font-medium text-white/90 mb-2 block">Business Name</Label>
              <Input
                data-testid="onboarding-business-name"
                value={brand.business_name}
                onChange={(e) => setBrand({...brand, business_name: e.target.value})}
                placeholder="e.g., Maine Mineral & Gem Museum"
                className="h-12 rounded-xl bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-afa-lime focus:ring-afa-lime mb-4"
              />

              <Label className="text-sm font-medium text-white/90 mb-2 block">Reply-To Email</Label>
              <Input
                data-testid="onboarding-reply-to"
                type="email"
                value={brand.reply_to_email}
                onChange={(e) => setBrand({...brand, reply_to_email: e.target.value})}
                placeholder="you@yourbusiness.com"
                className="h-12 rounded-xl bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-afa-lime focus:ring-afa-lime"
              />
              <p className="text-xs text-white/50 mt-2">When recipients reply to your newsletter, it lands here.</p>
            </div>
          )}

          {step === 2 && (
            <div data-testid="onboarding-step-2">
              <h2 className="text-2xl font-bold text-white mb-2" style={{fontFamily: 'Manrope'}}>Pick your sender handle</h2>
              <p className="text-white/70 mb-6">This is the email address your newsletters will come from.</p>

              <Label className="text-sm font-medium text-white/90 mb-2 block">Your Handle</Label>
              <div className="flex items-center rounded-xl border border-white/20 overflow-hidden h-12 bg-white/10 focus-within:ring-2 focus-within:ring-afa-lime">
                <Input
                  data-testid="onboarding-slug"
                  value={brand.slug || ""}
                  onChange={(e) => handleSlugChange(e.target.value)}
                  placeholder="yourbusiness"
                  className="border-0 rounded-none h-full bg-transparent text-white placeholder:text-white/40 focus-visible:ring-0"
                />
                <span className="bg-white/10 h-full px-4 flex items-center text-white/70 font-mono text-sm border-l border-white/10">
                  @{brand.shared_sender_domain || "swiftlett.com"}
                </span>
              </div>
              <div className="min-h-[1.5rem] mt-2">
                {slugStatus.checking && <span className="text-xs text-white/60">Checking…</span>}
                {!slugStatus.checking && slugStatus.available === true && (
                  <span className="text-xs text-afa-lime font-medium flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> {slugStatus.message}
                  </span>
                )}
                {!slugStatus.checking && slugStatus.available === false && (
                  <span className="text-xs text-amber-300 font-medium flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> {slugStatus.message}
                  </span>
                )}
                {!slugStatus.checking && slugStatus.available === null && (
                  <span className="text-xs text-white/50">Letters and numbers only. We'll suggest one if you leave it blank.</span>
                )}
              </div>

              {/* Preview */}
              <div className="mt-6 bg-white/5 rounded-xl p-4 font-mono text-xs">
                <p className="text-white/40 uppercase tracking-wider text-[10px] mb-2">Preview</p>
                <div className="text-white/80">
                  <span className="text-white/50">From:</span> <span className="text-white font-bold">{brand.business_name || "Your Business"}</span> &lt;{brand.slug || "yourbusiness"}@{brand.shared_sender_domain || "swiftlett.com"}&gt;
                </div>
                <div className="text-white/80 mt-1">
                  <span className="text-white/50">Reply-To:</span> <span className="text-afa-lime">{brand.reply_to_email || "you@example.com"}</span>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="onboarding-step-3" className="text-center py-4">
              <div className="w-16 h-16 bg-afa-lime/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-10 h-10 text-afa-lime" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2" style={{fontFamily: 'Manrope'}}>You're ready! 🎉</h2>
              <p className="text-white/70 mb-6">
                Your sender is set up as <span className="font-mono text-afa-lime">{brand.slug}@{brand.shared_sender_domain}</span> — now let's create your first newsletter.
              </p>

              <div className="bg-white/5 rounded-xl p-5 text-left mb-6">
                <p className="text-white/90 font-semibold mb-2" style={{fontFamily: 'Manrope'}}>Here's how it works:</p>
                <ol className="text-white/70 text-sm space-y-2">
                  <li><span className="text-afa-lime font-bold mr-1">1.</span> Paste any webpage URL — a blog post, news article, anything.</li>
                  <li><span className="text-afa-lime font-bold mr-1">2.</span> AI drafts a beautiful email — copy, layout, design, all done.</li>
                  <li><span className="text-afa-lime font-bold mr-1">3.</span> You easily customize — colors, photos, copy. Make it yours.</li>
                  <li><span className="text-afa-lime font-bold mr-1">4.</span> Upload contacts, hit Send. Done.</li>
                </ol>
              </div>
            </div>
          )}

          {/* Footer buttons */}
          <div className="flex gap-3 mt-8">
            {step > 1 && step < 3 && (
              <Button
                data-testid="onboarding-back-btn"
                variant="outline"
                onClick={() => setStep(step - 1)}
                className="rounded-full bg-white/10 border-white/20 text-white hover:bg-white/20"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
            )}
            {step < 3 && (
              <>
                <Button
                  data-testid="onboarding-skip-btn"
                  variant="ghost"
                  onClick={skipForNow}
                  className="text-white/60 hover:text-white hover:bg-white/10 rounded-full"
                >
                  Skip for now
                </Button>
                <Button
                  data-testid="onboarding-next-btn"
                  onClick={goNext}
                  disabled={saving || (step === 2 && slugStatus.available === false)}
                  className="ml-auto bg-afa-lime hover:bg-afa-lime/90 text-afa-navy rounded-full font-semibold px-6"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {step === 1 ? "Continue" : "Save & Continue"}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </>
            )}
            {step === 3 && (
              <>
                <Button
                  data-testid="onboarding-dashboard-btn"
                  variant="ghost"
                  onClick={skipForNow}
                  className="text-white/60 hover:text-white hover:bg-white/10 rounded-full"
                >
                  Take me to my Dashboard
                </Button>
                <Button
                  data-testid="onboarding-finish-btn"
                  onClick={finishOnboarding}
                  disabled={saving}
                  className="ml-auto bg-afa-lime hover:bg-afa-lime/90 text-afa-navy rounded-full font-semibold px-6"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Create my first newsletter <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </>
            )}
          </div>
        </div>

        <p className="text-center text-white/40 text-xs mt-6">
          We handle all the email infrastructure (via our Swiftlett engine) — no DNS setup, no spam worries.
        </p>
      </div>
    </div>
  );
};


// Settings Page — manages the user's Brand (what recipients see)

export default OnboardingWizard;
